import { useState, useContext } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAPI } from "../../hooks/useAPI";
import { useUI } from "../../contexts/UIContext";
import { useAuth } from "../../contexts/AuthContext";
import { BrandContext } from "../../App";

import { Alert } from "@mui/material";

import BorderCard from "../../components/BorderCard/BorderCard";
import DefaultBadge from "../simple/DefaultBadge";
import PageTitle from "../../components/PageTitle/PageTitle";
import SimFilter from "./SimFilter/SimFilter.js";
import SimTable from "./SimTable/SimTable";
import SimMobileCards from "./SimMobileCards/SimMobileCards";
import DefaultButton from "../../components/simple/DefaultButton";

import Modal from "../../components/Modal/Modal";

import CircularProgress from "@mui/material/CircularProgress";

import { useSimulationQueryParams } from "../../hooks/useSimulationQueryFilter";
import { Container } from "./style.js";

const SimsLoading = () => (
  <div className="sims-loading">
    <CircularProgress enableTrackSlot />
    <span>Loading...</span>
  </div>
);

const PastSimulations = () => {
  const queryClient = useQueryClient();

  const { isMobile, theme } = useUI();
  const { callAPI } = useAPI();
  const { authToken } = useAuth();

  const is2FA = window.location.pathname.toLowerCase().includes("2fa");

  const { featureFlags } = useContext(BrandContext);

  const [deletedId, setDeletedId] = useState(null);
  const [sortOrder, setSortOrder] = useState("default");
  const [apiMessage, setApiMessage] = useState({ type: "", msg: "" });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    show: false,
    id: null,
  });

  const {
    page,
    simType,
    dateRangeStart,
    dateRangeEnd,
    setPage,
    updateFilters,
    queryString,
    hasActiveFilters,
    applyFilters,
  } = useSimulationQueryParams();

  const {
    data = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["simulations", page, simType, dateRangeStart, dateRangeEnd],
    queryFn: () =>
      callAPI(`simulation?${queryString}`, {
        token: authToken,
      }),
    staleTime: 5 * 60 * 1000,
    enabled: !!authToken && !is2FA,
  });

  const deleteSimulation = async () => {
    try {
      await callAPI(`simulation/${deleteConfirmModal.id}`, {
        method: "DELETE",
        token: authToken,
      });
      setDeletedId(deleteConfirmModal.id);
      setApiMessage({ type: "success", msg: "Simulation has been deleted" });
      if (!isMobile)
        // removed in card component, no need to refetch
        queryClient.invalidateQueries({
          queryKey: [
            "simulations",
            page,
            simType,
            dateRangeStart,
            dateRangeEnd,
          ],
        });
    } catch (error) {
      setApiMessage({
        type: "error",
        msg: error.message || error.detail || "Unable to delete simulation",
      });
    } finally {
      setDeleteConfirmModal({ show: false, id: null });
      if (isMobile) window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <Container id="past-simulations">
      <PageTitle text="Your Performance Spotlight" topText="Review your progress">
        Select a session below to view your results and replay your feedback
        anytime.
      </PageTitle>

      {error && (
        <Alert severity="error" sx={{ mt: 4, mb: 4 }}>
          {error?.message || "An unexpected error has occured"}
        </Alert>
      )}

      {apiMessage.type !== "" && apiMessage.msg !== "" && (
        <Alert severity={apiMessage.type} sx={{ mb: 4, mt: 4 }}>
          {apiMessage.msg}
        </Alert>
      )}

      {!isMobile ? (
        <BorderCard
          title="Past Simulations"
          subTitle={
            <DefaultBadge
              borderColor="#A87EFF"
              size="small"
              variant="pillTransparent"
              sx={{ color: "#fff" }}
            >
              {data?.pagination?.total_items || 0} completed
            </DefaultBadge>
          }
          actions={
            data?.data?.length ? (
              <SimFilter
                simType={simType}
                dateRange={{ dateRangeStart, dateRangeEnd }}
                updateFilters={updateFilters}
                isMobile={isMobile}
                sortOrder={sortOrder}
                totalResults={data.data.length}
                applyFilters={applyFilters}
                featureFlags={featureFlags}
              />
            ) : null
          }
          narrowContainer
          inlineSubTitle
        >
          {!isLoading ? (
            <SimTable
              simulations={data.data}
              theme={theme}
              paginationData={data.pagination}
              page={page}
              onPageChange={setPage}
              setDeleteConfirmModal={setDeleteConfirmModal}
              updateFilters={updateFilters}
              hasActiveFilters={hasActiveFilters}
            />
          ) : (
            <SimsLoading />
          )}
        </BorderCard>
      ) : (
        <div id="mobile-past-sims">
          <div className="heading-top">
            <PageTitle variant="h2" text="Past Simulations" />
            <DefaultBadge
              borderColor="#A87EFF"
              size="small"
              variant="pillTransparent"
              sx={{ color: "#fff" }}
            >
              {data?.pagination?.total_items || 0} completed
            </DefaultBadge>
          </div>
          {data?.data?.length ? (
            <SimFilter
              simType={simType}
              dateRange={{ dateRangeStart, dateRangeEnd }}
              updateFilters={updateFilters}
              isMobile={isMobile}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              totalResults={data.data.length}
              applyFilters={applyFilters}
              featureFlags={featureFlags}
            />
          ) : null}

          {isLoading && page === 0 ? (
            <SimsLoading />
          ) : (
            <SimMobileCards
              simulations={data?.data || []} // ADDED: Fallback to an empty array
              page={page}
              paginationData={data?.pagination} // Added optional chaining here just in case
              theme={theme}
              onPageChange={setPage}
              sortOrder={sortOrder}
              setDeleteConfirmModal={setDeleteConfirmModal}
              isLoading={isLoading}
              deletedId={deletedId}
              updateFilters={updateFilters}
              hasActiveFilters={hasActiveFilters}
            />
          )}
        </div>
      )}
      {deleteConfirmModal.show && (
        <Modal
          title="Delete Simulation"
          open={deleteConfirmModal.show}
          onClose={() => setDeleteConfirmModal({ show: false, id: null })}
          showIcon
          slim
          icon={
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16 6V5.2C16 4.0799 16 3.51984 15.782 3.09202C15.5903 2.71569 15.2843 2.40973 14.908 2.21799C14.4802 2 13.9201 2 12.8 2H11.2C10.0799 2 9.51984 2 9.09202 2.21799C8.71569 2.40973 8.40973 2.71569 8.21799 3.09202C8 3.51984 8 4.0799 8 5.2V6M10 11.5V16.5M14 11.5V16.5M3 6H21M19 6V17.2C19 18.8802 19 19.7202 18.673 20.362C18.3854 20.9265 17.9265 21.3854 17.362 21.673C16.7202 22 15.8802 22 14.2 22H9.8C8.11984 22 7.27976 22 6.63803 21.673C6.07354 21.3854 5.6146 20.9265 5.32698 20.362C5 19.7202 5 18.8802 5 17.2V6"
                stroke="#D92D20"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        >
          <p>
            This will permanently delete this record from your Performance
            Spotlight. You won't be able to restore it once deleted.
          </p>
          <div className="button-group">
            <DefaultButton
              className="action-button"
              onClick={() => setDeleteConfirmModal({ show: false, id: null })}
              wideOnMobile
            >
              Cancel
            </DefaultButton>
            <button
              className="confirm-delete"
              onClick={() => deleteSimulation()}
            >
              Delete Simulation
            </button>
          </div>
        </Modal>
      )}
    </Container>
  );
};

export default PastSimulations;
